import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";
import Landing from "@/components/Landing";
import QuizRunner from "@/components/QuizRunner";
import EmailCapture from "@/components/EmailCapture";
import Results from "@/components/Results";

const STAGES = { LANDING: "landing", QUIZ: "quiz", EMAIL: "email", RESULTS: "results" };

function newId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return "sub-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export default function PublicApp() {
  const [stage, setStage] = useState(STAGES.LANDING);
  const [answers, setAnswers] = useState(Array(8).fill(null));
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [settings, setSettings] = useState({ prospectus_url: "", booking_url: "", privacy_url: "" });
  const submissionId = useRef(newId());

  useEffect(() => {
    api
      .get("/settings/public")
      .then((res) => setSettings(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [stage]);

  const start = () => {
    setAnswers(Array(8).fill(null));
    submissionId.current = newId();
    setStage(STAGES.QUIZ);
  };

  const submit = async ({ firstName, email, choice }) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/quiz/submit", {
        submission_id: submissionId.current,
        first_name: firstName,
        email,
        marketing_choice: choice,
        answers,
      });
      setResult(data);
      setStage(STAGES.RESULTS);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "We couldn’t save your results. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => {
    setResult(null);
    setAnswers(Array(8).fill(null));
    submissionId.current = newId();
    setStage(STAGES.LANDING);
  };

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <SiteHeader onStart={stage === STAGES.LANDING ? start : null} />
      {stage === STAGES.LANDING && <Landing onStart={start} />}
      {stage === STAGES.QUIZ && (
        <QuizRunner
          answers={answers}
          setAnswers={setAnswers}
          onComplete={() => setStage(STAGES.EMAIL)}
          onExit={() => setStage(STAGES.LANDING)}
        />
      )}
      {stage === STAGES.EMAIL && (
        <EmailCapture onSubmit={submit} submitting={submitting} privacyUrl={settings.privacy_url} />
      )}
      {stage === STAGES.RESULTS && result && (
        <Results result={result} settings={settings} onRestart={restart} />
      )}
      <footer className="border-t border-[#e2ded7] py-8 mt-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-[#5a686b]">
          The Weave Founder Academy · Founder First; Business Second.
        </div>
      </footer>
    </div>
  );
}
